import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Stack,
  Button,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as CsvIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY, SENTIMENT_API_BASE_URL } from '../../services/api';
import { decryptResponse, downloadPDF, encryptResponse, getSessionKey } from '../../services';
import { themeBackgrounds } from '../../theme/theme';
import PageHeader from '../../components/PageHeader';
import { FREE_PLAN_ID } from '../../utils/common';
import { useNavigate } from 'react-router-dom';
import AnalyticsContent from '../../components/dashboard/AnalyticsContent';
import previewParticipantLevelAnalyticsData from '../../data/previewParticipantLevelSentimentAnalyticsData.json';
import gridDataParticipantLevelSentimentAnalytics from '../../data/gridDataParticipantLevelSentimentAnalytics.json';
import { previewParticipantLevelSentimentAnalyticsStyleLayout } from '../../configs/previewParticipantLevelSentimentAnalyticsStyleConfig';
import { previewParticipantLevelSentimentAnalyticsDataConfigs } from '../../configs/previewParticipantLevelSentimentAnalyticsDataConfig';
import CustomAgGridDailogBox from '../../components/CustomAgGridDailogBox';

const SentimentParticipantAnalytics: React.FC = () => {
  const [activeTab, setActiveTab] = useState('participantLevelAnalytics');
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const [participantAnalyticsData, setParticipantAnalyticsData] = useState<any>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('all_apps');
  const [applications, setApplications] = useState<any[]>([{ appId: 'all_apps', appName: 'All Applications' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freePlanDialogOpen, setFreePlanDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [dateRange, setDateRange] = useState('last7days');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [tempFromDate, setTempFromDate] = useState<Date | null>(null);
  const [tempToDate, setTempToDate] = useState<Date | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [gridDialogOpen, setGridDialogOpen] = useState(false);
  const [gridDialogTitle, setGridDialogTitle] = useState('');
  const [gridDialogData, setGridDialogData] = useState<any[]>([]);
  const [columnDefs, setColumnDefs] = useState<any[]>([
    { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
  ]);

  useEffect(() => {
    const range = getDateRange('last7days');
    setFromDate(range.from);
    setToDate(range.to);
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [userDetails]);

  useEffect(() => {
    if (userDetails?.plan_details.plan_id === FREE_PLAN_ID && !previewMode) {
      setPreviewMode(true);
      setFreePlanDialogOpen(true);
    }
  }, [userDetails, previewMode]);

  useEffect(() => {
    if (fromDate && toDate && userDetails?.plan_details.plan_id !== FREE_PLAN_ID && !previewMode) {
      fetchDataForActiveTab();
    }
  }, [activeTab, fromDate, toDate, previewMode]);

  const fetchSentimentParticipantAnalyticsData = async () => {
    if (previewMode) {
      return;
    }
    if (!userDetails?.organization_id || !fromDate || !toDate) {
      setError('Missing required user data or date range');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const params: any = {
        org_id: userDetails.organization_id,
        date_from: fromDate.toISOString().split('T')[0],
        date_to: toDate.toISOString().split('T')[0]
      };
      if (selectedApplicationId !== 'all_apps') {
        params.application_id = selectedApplicationId;
      }

      type ParticipantLevelSentimentAnalyticsResponse = { status: number; data: any;[key: string]: any };
      const response = await httpService.get<ParticipantLevelSentimentAnalyticsResponse>(`${SENTIMENT_API_BASE_URL}/api/participants/analytics`, { params });

      if (response.status === 200) {
        setParticipantAnalyticsData(response.data);
      } else {
        setError('Failed to fetch user analytics data');
      }
    } catch (error) {
      setError('Network error while fetching data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataForActiveTab = () => {
    switch (activeTab) {
      case 'participantLevelAnalytics':
        fetchSentimentParticipantAnalyticsData();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (userDetails?.plan_details.plan_id !== FREE_PLAN_ID) {
      fetchDataForActiveTab();
    }
  }, [userDetails, selectedApplicationId]);

  const getAvailableReports = () => {
    switch (activeTab) {
      case 'participantLevelAnalytics':
        return [
          { id: 'counts-analytics', name: 'User Growth Over Time', description: 'Monthly user acquisition and retention data' },
          { id: 'top-ten-chat-users-graph', name: 'Top ten Chat Users', description: 'Most active users in chat' },
          { id: 'top-ten-chat-groups-graph', name: 'Top ten Chat Groups', description: 'Most active chat groups' },
          { id: 'new-user-graph-by-days', name: 'New Users by Day', description: 'User sign-ups by day' },
          { id: 'online-frequency-distribution-graph', name: 'User Online Frequency', description: 'User online frequency analysis' },
          { id: 'online-users-per-day-graph', name: 'Activity by Hour', description: 'Peak usage hours throughout the day' },
          { id: 'chat-status-distribution-graph', name: 'Chat Status Distribution', description: 'Distribution of chat statuses (e.g., sent, delivered, read)' },
          { id: 'group-wise-participants-counts-graph', name: 'Group-wise Participants', description: 'User participation in different groups' }
        ];
      default:
        return [];
    }
  };

  // File format options
  const fileFormats = [
    { value: 'pdf', label: 'PDF Document', icon: <PdfIcon />, description: 'Formatted report with charts and graphics' },
    { value: 'excel', label: 'Excel Spreadsheet', icon: <ExcelIcon />, description: 'Raw data in spreadsheet format' },
    { value: 'csv', label: 'CSV File', icon: <CsvIcon />, description: 'Comma-separated values for data analysis' }
  ];

  // Date range options
  const dateRangeOptions = [
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last14days', label: 'Last 14 days' },
    { value: 'last4weeks', label: 'Last 4 weeks' },
    { value: 'custom', label: 'Custom range' },
  ];

  // Function to calculate date ranges
  const getDateRange = (option: string) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    switch (option) {
      case 'last7days':
        const last7days = new Date(today);
        last7days.setDate(last7days.getDate() - 7);
        return { from: last7days, to: today };
      case 'last14days':
        const last14days = new Date(today);
        last14days.setDate(last14days.getDate() - 14);
        return { from: last14days, to: today };
      case 'last4weeks':
        const last4weeks = new Date(today);
        last4weeks.setDate(last4weeks.getDate() - 28);
        return { from: last4weeks, to: today };
      default:
        return { from: fromDate, to: toDate };
    }
  };

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    if (value === 'custom') {
      setCustomDateOpen(true);
      setTempFromDate(fromDate);
      setTempToDate(toDate);
    } else {
      const range = getDateRange(value);
      setFromDate(range.from);
      setToDate(range.to);
    }
  };

  const handleCustomDateApply = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setCustomDateOpen(false);
  };

  const handleCustomDateCancel = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setCustomDateOpen(false);
    setDateRange(fromDate && toDate ? 'custom' : 'last7days');
  };

  const fetchApplications = async () => {
    const payload = {
      request_id: "notegtest1",
      timestamp: new Date().toISOString(),
      action: "get_all_applications",
      data: await encryptResponse({
        organization_id: userDetails?.organization_id || '',
        user_id: userDetails?.user_id || '',
      }, REQ_ENC_KEY)
    };
    try {
      const response = await httpService.post<any>(`${API_BASE_URL}/user/getAllApplications`, payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
      const decrypted = await decryptResponse<{
        applications: any[];
      }>(data, getSessionKey() as string);
        const { applications } = decrypted;
        const fetchedApplications = applications || [];
        const appData = fetchedApplications.map((app: any) => ({ appId: app.app_id, appName: app.app_name }));
        const updatedApplications = [{ appId: 'all_apps', appName: 'All Applications' }, ...appData];
        setApplications(updatedApplications);
      }
    } catch (error) {
      setApplications([{ appId: 'all_apps', appName: 'All Applications' }]);
    }
  };

  const handleDetailsClick = async (data?: any) => {
    console.log('Details clicked:', data);
    if (previewMode) {
      if (data?.key === 'attention_required') {
        const dataConfig = (gridDataParticipantLevelSentimentAnalytics as any).attention_required_grid;
        const colDefs = dataConfig.grid_config.columns.map((col: any) => ({
          field: col.field,
          headerName: col.header,
          minWidth: col.width || 100
        }));
        setColumnDefs([
          { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
          ...colDefs
        ]);
        const severityLevel = data?.itemData?.severity
          ? data.itemData.severity.charAt(0).toUpperCase() + data.itemData.severity.slice(1)
          : 'All';
        const alertCount = data?.itemData?.count || 0;
        setGridDialogTitle(`Attention Required - ${severityLevel} (${alertCount} participant${alertCount !== 1 ? 's' : ''})`);
        setGridDialogData(alertCount === 0 ? [] : dataConfig.rows);
        setGridDialogOpen(true);
      }
      if (data?.key === 'workload_distribution') {
        const dataConfig = (gridDataParticipantLevelSentimentAnalytics as any).workload_distribution_grid;
        const colDefs = dataConfig.grid_config.columns.map((col: any) => ({
          field: col.field,
          headerName: col.header,
          minWidth: col.width || 100
        }));
        setColumnDefs([
          { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
          ...colDefs
        ]);
        const rangeLabel = data?.itemData?.range || 'All Ranges';
        const participantCount = data?.itemData?.count || 0;
        setGridDialogTitle(`Workload Distribution - ${rangeLabel} calls (${participantCount} participant${participantCount !== 1 ? 's' : ''})`);
        setGridDialogData(participantCount === 0 ? [] : dataConfig.rows);
        setGridDialogOpen(true);
      }
      if (data?.key === 'performance_trend') {
        const dataConfig = (gridDataParticipantLevelSentimentAnalytics as any).performance_score_grid;
        const colDefs = dataConfig.grid_config.columns.map((col: any) => ({
          field: col.field,
          headerName: col.header_name,
          minWidth: col.width || 100
        }));
        setColumnDefs([
          { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
          ...colDefs
        ]);
        const participantName = data?.itemData?.topic || 'All Participants';
        const score = data?.itemData?.percentage || 0;
        const trendInfo = data?.itemData?.info || '';
        setGridDialogTitle(`Performance Trend - ${participantName} (Score: ${score})`);
        setGridDialogData(dataConfig.participants || []);
        setGridDialogOpen(true);
      }
    } else {
      if (data?.key === 'attention_required') {
        const severity = data?.itemData?.severity ? String(data.itemData.severity).toLowerCase() : 'all';
        await getAttentionRequiredGridData(severity);
      } else if (data?.key === 'workload_distribution') {
        const range = data?.itemData?.range ? String(data.itemData.range) : '';
        await getWorkloadDistributionGridData(range);
      } else if (data?.key === 'performance_trend') {
        const participantId = data?.itemData?.participant_id || data?.itemData?.participantId || data?.itemData?.id || data?.itemData?.topic || '';
        if (participantId) {
          await getPerformanceTrendGridData(String(participantId));
        } else {
          setError('Participant id missing for performance trend');
        }
      }
    }
  };

  const getPerformanceTrendGridData = async (participantId: string) => {
    // setIsLoading(true);
    setError(null);
    try {
      if (!userDetails?.organization_id || !fromDate || !toDate || !participantId) {
        setError('Missing required user data, date range, or participant id');
        return;
      }

      const params: any = {
        org_id: userDetails.organization_id,
        date_from: fromDate.toISOString().split('T')[0],
        date_to: toDate.toISOString().split('T')[0],
        page: 1,
        page_size: 25
      };
      if (selectedApplicationId && selectedApplicationId !== 'all_apps') {
        params.application_id = selectedApplicationId;
      }

      const url = `${SENTIMENT_API_BASE_URL}/api/participants/grid/trend/${encodeURIComponent(participantId)}/calls`;
      const response = await httpService.get<any>(url, { params });

      if (response.status === 200) {
        const data = response.data || {};

        const cols = data.grid_config?.columns || [];
        const colDefs = cols.map((col: any) => ({
          field: col.field,
          headerName: col.header || col.header_name || col.field,
          minWidth: col.width || 100
        }));

        setColumnDefs([
          { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
          ...colDefs
        ]);

        const title = data.title || `Call History - ${data.participant_name || participantId}`;
        setGridDialogTitle(title);
        setGridDialogData(Array.isArray(data.rows) ? data.rows : []);
        setGridDialogOpen(true);
      } else {
        setError('Failed to fetch performance trend grid data');
      }
    } catch (err) {
      setError('Network error while fetching performance trend data');
    } finally {
      setIsLoading(false);
    }
  };

  const getWorkloadDistributionGridData = async (range: string) => {
    // setIsLoading(true);
    setError(null);
    try {
      if (!userDetails?.organization_id || !fromDate || !toDate || !range) {
        setError('Missing required user data, date range, or bucket range');
        return;
      }

      const rangeParam = String(range);
      const params: any = {
        org_id: userDetails.organization_id,
        date_from: fromDate.toISOString().split('T')[0],
        date_to: toDate.toISOString().split('T')[0],
        page: 1,
        page_size: 25
      };
      if (selectedApplicationId && selectedApplicationId !== 'all_apps') {
        params.application_id = selectedApplicationId;
      }

      const url = `${SENTIMENT_API_BASE_URL}/api/participants/grid/workload/${encodeURIComponent(rangeParam)}`;
      const response = await httpService.get<any>(url, { params });

      if (response.status === 200) {
        const data = response.data || {};

        const cols = data.grid_config?.columns || [];
        const colDefs = cols.map((col: any) => ({
          field: col.field,
          headerName: col.header || col.header_name || col.field,
          minWidth: col.width || 100
        }));

        setColumnDefs([
          { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
          ...colDefs
        ]);

        const bucketLabel = data.bucket_info?.range_label || rangeParam;
        const bucketCount = data.bucket_info?.count ?? (Array.isArray(data.rows) ? data.rows.length : 0);
        setGridDialogTitle(data.title || `Workload - ${bucketLabel} (${bucketCount} participant${bucketCount !== 1 ? 's' : ''})`);
        setGridDialogData(Array.isArray(data.rows) ? data.rows : []);
        setGridDialogOpen(true);
      } else {
        setError('Failed to fetch workload distribution grid data');
      }
    } catch (err) {
      setError('Network error while fetching workload grid data');
    } finally {
      setIsLoading(false);
    }
  };

  const getAttentionRequiredGridData = async (severity: string) => {
    // setIsLoading(true);
    setError(null);
    try {
      if (!userDetails?.organization_id || !fromDate || !toDate || severity === 'all') {
        setError('Missing required user data or date range');
        return;
      }

      const sevRaw = severity ? String(severity).toLowerCase() : 'all';
      const severityMap: Record<string, string> = {
        critical: 'critical',
        high: 'warning',
        medium: 'info',
        all: 'all'
      };
      const sev = severityMap[sevRaw] || sevRaw;
      const params: any = {
        org_id: userDetails.organization_id,
        date_from: fromDate.toISOString().split('T')[0],
        date_to: toDate.toISOString().split('T')[0],
        page: 1,
        page_size: 25
      };
      if (selectedApplicationId && selectedApplicationId !== 'all_apps') {
        params.application_id = selectedApplicationId;
      }

      const url = `${SENTIMENT_API_BASE_URL}/api/participants/grid/attention/${encodeURIComponent(sev)}`;
      const response = await httpService.get<any>(url, { params });

      if (response.status === 200) {
        const data = response.data || {};

        const cols = data.grid_config?.columns || [];
        const colDefs = cols.map((col: any) => ({
          field: col.field,
          headerName: col.header || col.header_name || col.field,
          minWidth: col.width || 100
        }));

        setColumnDefs([
          { field: "checkbox", headerName: "", checkboxSelection: true, headerCheckboxSelection: true, width: 50, pinned: 'left', filter: false },
          ...colDefs
        ]);

        setGridDialogTitle(data.title || `Attention Required - ${sev.charAt(0).toUpperCase() + sev.slice(1)}`);
        setGridDialogData(Array.isArray(data.rows) ? data.rows : []);
        setGridDialogOpen(true);
      } else {
        setError('Failed to fetch attention required grid data');
      }
    } catch (err) {
      setError('Network error while fetching grid data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseGridDialog = () => {
    setGridDialogOpen(false);
  };

  const getCurrentDateRange = () => {
    const range = getDateRange(dateRange);
    if (!range.from || !range.to) return '';

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    if (range.from.toDateString() === range.to.toDateString()) {
      return formatDate(range.from);
    }
    return `${formatDate(range.from)} - ${formatDate(range.to)}`;
  };

  const handleReportSelection = (reportId: string) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  const handleSelectAllReports = () => {
    const allReportIds = getAvailableReports().map(report => report.id);
    if (selectedReports.length === allReportIds.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(allReportIds);
    }
  };

  const getChartsForSelectedReports = () => {
    const charts: any[] = [];

    selectedReports.forEach(reportId => {
      switch (activeTab) {
        case 'participantLevelAnalytics':
          switch (reportId) {
            case 'counts-analytics':
              if (participantAnalyticsData?.new_users_by_date?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'New Users by Date',
                  description: 'This bar chart shows the number of new users who joined each day within the selected date range.',
                  data: participantAnalyticsData.new_users_by_date.chart_data.map((item: any) => ({
                    name: item.date,
                    count: item.count
                  })),
                  dataKeys: ['count'],
                  xAxisKey: 'name',
                  colors: ['#F59E0B']
                });
              }
              break;
            case 'top-ten-chat-users-graph':
              break;
            case 'top-ten-chat-groups-graph':
              if (participantAnalyticsData?.online_frequency_distribution?.chart_data) {
                charts.push({
                  type: 'bar',
                  title: 'Online Frequency Distribution',
                  description: 'This bar chart displays how often users are online during different time intervals.',
                  data: participantAnalyticsData.online_frequency_distribution.chart_data.map((item: any) => ({
                    name: item.interval,
                    value: item.user_count
                  })),
                  dataKeys: ['value'],
                  xAxisKey: 'name',
                  colors: ['#8B5CF6']
                });
              }
              break;
            case 'new-user-graph-by-days':
              if (participantAnalyticsData?.top_chat_users?.chart_data) {
                charts.push({
                  type: 'pie',
                  title: 'Top 10 Chat Users',
                  description: 'This pie chart displays the top 10 users who have sent the most messages during the selected time frame.',
                  data: participantAnalyticsData.top_chat_users.chart_data.map((item: any) => ({
                    name: item.participant_name,
                    value: item.value,
                    color: item.color
                  }))
                });
              }
              break;
            case 'online-frequency-distribution-graph':
              if (participantAnalyticsData?.top_groups?.chart_data) {
                charts.push({
                  type: 'pie',
                  title: 'Top 10 Chat Groups',
                  description: 'This pie chart illustrates the top 10 groups that have received the most messages during the selected time frame.',
                  data: participantAnalyticsData.top_groups.chart_data.map((item: any) => ({
                    name: item.group_name,
                    value: item.value,
                    color: item.color
                  }))
                });
              }
              break;
            case 'online-users-per-day-graph':
              if (participantAnalyticsData?.online_users_per_day?.chart_data) {
                charts.push({
                  type: 'line',
                  title: 'Online Users Per Day',
                  description: 'This line chart shows the number of unique online users and total online events per day.',
                  data: participantAnalyticsData.online_users_per_day.chart_data.map((item: any) => ({
                    name: item.date,
                    users: item.unique_users_count,
                    events: item.total_online_events
                  })),
                  dataKeys: ['users', 'events'],
                  xAxisKey: 'name',
                  colors: ['#3B82F6', '#10B981']
                });
              }
              break;
            case 'chat-status-distribution-graph':
              if (participantAnalyticsData?.chat_status_counts?.chat_counts) {
                charts.push({
                  type: 'pie',
                  title: 'Chat Status Distribution',
                  description: 'This pie chart shows the distribution of different chat statuses.',
                  data: participantAnalyticsData.chat_status_counts.chat_counts.map((item: any) => ({
                    name: item.status,
                    value: item.count,
                    color: item.status === 'active' ? '#10B981' : item.status === 'inactive' ? '#EF4444' : '#F59E0B'
                  }))
                });
              }
              break;
            case 'group-wise-participants-counts-graph':
              if (participantAnalyticsData?.chat_status_counts?.chat_counts) {
                charts.push({
                  type: 'pie',
                  title: 'Chat Status Distribution',
                  description: 'This pie chart shows the distribution of different chat statuses.',
                  data: participantAnalyticsData.chat_status_counts.chat_counts.map((item: any) => ({
                    name: item.status,
                    value: item.count,
                    color: item.status === 'active' ? '#10B981' : item.status === 'inactive' ? '#EF4444' : '#F59E0B'
                  }))
                });
              }
              break;
          }
          break;
        default:
          break;
      }
    });

    return charts;
  };

  const handleDownloadReport = () => {
    if (selectedReports.length === 0) {
      alert('Please select at least one report to download.');
      return;
    }

    const selectedReportNames = getAvailableReports()
      .filter(report => selectedReports.includes(report.id))
      .map(report => report.name);

    const charts = getChartsForSelectedReports();

    if (charts.length === 0) {
      alert('No data available for the selected reports. Please try again later.');
      return;
    }

    const dateRangeStr = getCurrentDateRange().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    const filename = `${activeTab}_report_${dateRangeStr}.pdf`;

    downloadPDF({
      data: null,
      filename,
      title: `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Report`,
      format: selectedFormat as 'pdf',
      dateRange: getCurrentDateRange(),
      tab: activeTab,
      selectedReports: selectedReportNames,
      charts: charts
    });

    setReportDialogOpen(false);
    setSelectedReports([]);
  };

  const handleOpenReportDialog = () => {
    setReportDialogOpen(true);
    setSelectedReports([]);
  };

  const handlePreviewAnalytics = () => {
    setPreviewMode(true);
    setFreePlanDialogOpen(false);
  };

  const handleCancelFreePlan = () => {
    setFreePlanDialogOpen(false);
  };

  const renderParticipantSentimentAnalytics = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!participantAnalyticsData && !previewMode && !previewMode) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">
            No data available. Please check your date range and try again.
          </Typography>
        </Box>
      );
    }

    if (previewMode) {
      return (
        <>
          <AnalyticsContent dataConfigs={previewParticipantLevelSentimentAnalyticsDataConfigs} styleLayout={previewParticipantLevelSentimentAnalyticsStyleLayout} analyticsData={previewParticipantLevelAnalyticsData} onDetailsClick={handleDetailsClick} />
          <CustomAgGridDailogBox
            open={gridDialogOpen}
            onClose={handleCloseGridDialog}
            title={gridDialogTitle}
            rowData={gridDialogData}
            columnDefs={columnDefs}
            rowSelection='multiple'
            containerStyle={{
              height: '70vh'
            }}
            gridOptions={{
              suppressDragLeaveHidesColumns: true
            }}
            onRowSelected={(params) => {
              console.log('Selected Rows::', params.api.getSelectedRows());
            }}
            onCellClicked={(params) => {
              console.log('Cell clicked:', params);
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              flex: 1,
              cellStyle: { textAlign: 'left' }
            }}
          />
        </>
      );
    }

    return (
      <>
        <AnalyticsContent dataConfigs={previewParticipantLevelSentimentAnalyticsDataConfigs} styleLayout={previewParticipantLevelSentimentAnalyticsStyleLayout} analyticsData={participantAnalyticsData} onDetailsClick={handleDetailsClick} />
        <CustomAgGridDailogBox
          open={gridDialogOpen}
          onClose={handleCloseGridDialog}
          title={gridDialogTitle}
          rowData={gridDialogData}
          columnDefs={columnDefs}
          rowSelection='multiple'
          containerStyle={{
            height: '70vh'
          }}
          gridOptions={{
            suppressDragLeaveHidesColumns: true
          }}
          onRowSelected={(params) => {
            console.log('Selected Rows::', params.api.getSelectedRows());
          }}
          onCellClicked={(params) => {
            console.log('Cell clicked:', params);
          }}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
            cellStyle: { textAlign: 'left' }
          }}
        />
      </>
    );
  };

  const tabItems = [
    { id: 'participantLevelAnalytics', label: 'Participants-Level-Analytics', icon: <AnalyticsIcon /> },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <PageHeader
          heading="Interaction Intelligence Platform"
          subheading="Monitor your application performance, user engagement, and communication statistics"
          variant="gradient"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {/* <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleOpenReportDialog}
                sx={{
                  background: themeBackgrounds.default,
                  color: '#ffffff',
                  '&:hover': {
                    background: themeBackgrounds.default,
                    opacity: 0.9,
                  },
                }}
              >
                Download Report
              </Button> */}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedApplicationId}
                  onChange={(e) => setSelectedApplicationId(e.target.value)}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {applications.map((app) => (
                    <MenuItem key={app.appId} value={app.appId}>
                      {app.appName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {applications.find(app => app.appId === selectedApplicationId)?.appName || 'Select Application'}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  startAdornment={<CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  {dateRangeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {getCurrentDateRange() && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {getCurrentDateRange()}
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Dialog
        open={freePlanDialogOpen}
        onClose={handleCancelFreePlan}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box
                sx={{
                  backgroundColor: 'primary.main',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AnalyticsIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ lineHeight: 1.3 }}>
                  Analytics Not Available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  on Free Plan
                </Typography>
              </Box>
            </Stack>
            <Button
              onClick={handleCancelFreePlan}
              sx={{
                minWidth: 'auto',
                p: 0.75,
                color: 'text.secondary',
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Analytics features are available only on paid plans. Upgrade your plan to unlock full access to real-time insights, usage reports, and detailed analytics for your application.
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '100px',
                  height: '100px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography sx={{ fontSize: 24, flexShrink: 0 }}>💡</Typography>
                <Box>
                  <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5 }}>
                    Preview Available
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95, fontSize: '0.875rem' }}>
                    You can still preview how analytics will look by viewing sample analytics data.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handlePreviewAnalytics}
            variant="outlined"
            fullWidth
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.lighter'
              }
            }}
          >
            <AnalyticsIcon sx={{ mr: 1, fontSize: 20 }} />
            Preview Analytics
          </Button>
          <Button
            onClick={() => navigate('/plans-and-billing')}
            variant="contained"
            fullWidth
            sx={{
              py: 1.25,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                background: 'linear-gradient(135deg, #5568d3 0%, #6b3f91 100%)'
              }
            }}
          >
            <TrendingUpIcon sx={{ mr: 1, fontSize: 20 }} />
            Upgrade Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={customDateOpen}
        onClose={handleCustomDateCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="From Date"
              type="date"
              value={tempFromDate ? tempFromDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setTempFromDate(e.target.value ? new Date(e.target.value) : null)}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="To Date"
              type="date"
              value={tempToDate ? tempToDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setTempToDate(e.target.value ? new Date(e.target.value) : null)}
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: tempFromDate ? tempFromDate.toISOString().split('T')[0] : undefined,
                max: new Date().toISOString().split('T')[0]
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCustomDateApply}
            variant="contained"
            disabled={!tempFromDate || !tempToDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Download Reports</Typography>
            <Chip
              label={`${tabItems.find(tab => tab.id === activeTab)?.label || 'Reports'}`}
              size="small"
              color="primary"
            />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Date Range: {getCurrentDateRange() || 'No date range selected'}
              </Typography>
            </Box>

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Select Reports</Typography>
                <Button
                  size="small"
                  onClick={handleSelectAllReports}
                  sx={{ textTransform: 'none' }}
                >
                  {selectedReports.length === getAvailableReports().length ? 'Deselect All' : 'Select All'}
                </Button>
              </Stack>

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List>
                  {getAvailableReports().map((report, index) => (
                    <React.Fragment key={report.id}>
                      <ListItem sx={{ py: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedReports.includes(report.id)}
                              onChange={() => handleReportSelection(report.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" fontWeight="medium">
                                {report.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {report.description}
                              </Typography>
                            </Box>
                          }
                          sx={{ width: '100%', margin: 0 }}
                        />
                      </ListItem>
                      {index < getAvailableReports().length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>

              {selectedReports.length > 0 && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  {selectedReports.length} report(s) selected
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>File Format</Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                >
                  {fileFormats.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {format.icon}
                        <Box>
                          <Typography variant="body1">{format.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDownloadReport}
            variant="contained"
            disabled={selectedReports.length === 0}
            startIcon={<DownloadIcon />}
          >
            Download {selectedReports.length > 0 ? `(${selectedReports.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={0}>
            {tabItems.map((tab) => (
              <Button
                key={tab.id}
                startIcon={tab.icon}
                onClick={() => setActiveTab(tab.id)}
                disabled={isLoading}
                sx={{
                  py: 2,
                  px: 3,
                  borderRadius: 0,
                  textTransform: 'none',
                  fontSize: '1.2rem',
                  color: activeTab === tab.id ? 'primary.main' : 'text.secondary',
                  borderBottom: activeTab === tab.id ? 2 : 0,
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {tab.label}
              </Button>
            ))}
          </Stack>
        </Box>
      </Paper>

      {previewMode && (
        <Paper
          sx={{
            mb: 2,
            p: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <AnalyticsIcon sx={{ fontSize: 28 }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Preview Mode - Sample Data
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  You are viewing sample analytics data. Upgrade your plan to access real-time analytics.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/plans-and-billing')}
              sx={{
                color: 'white',
                borderColor: 'white',
                flexShrink: 0,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Upgrade Plan
            </Button>
          </Stack>
        </Paper>
      )}

      <Box sx={{ width: '100%', background: themeBackgrounds.lightGrey }}>
        <Box sx={{ padding: 1 }}>
          {activeTab === 'participantLevelAnalytics' && renderParticipantSentimentAnalytics()}
        </Box>
      </Box>
    </Box>
  );
}

export default SentimentParticipantAnalytics;
